import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, Users, User, Mail, Leaf, ChefHat, MapPin, Loader2, CheckCircle2, Check, X, FileText, Cake, AlertCircle, ChevronLeft, ChevronRight, Globe, Search } from 'lucide-react';
import { supabase } from "../lib/supabaseClient";
import ReCAPTCHA from "react-google-recaptcha";
import { format, eachDayOfInterval, isSameDay, isBefore, isAfter, startOfMonth, endOfMonth, addMonths, subMonths, isWithinInterval, startOfWeek, addDays, getDay } from "date-fns";

// Import your logo
import logo from '../assets/haibeirut_logo.png';
import restaurantBackground from '../assets/bg_image.png';

// Get reCAPTCHA site key from environment (for Create React App)
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const countryCodes = [
  // GCC Countries
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', pattern: /^[0-9]{9}$/, example: '5XXXXXXXX', maxLength: 9 },
  { code: '+971', country: 'UAE', flag: '🇦🇪', pattern: /^[0-9]{9}$/, example: '5XXXXXXXX', maxLength: 9 },
  { code: '+974', country: 'Qatar', flag: '🇶🇦', pattern: /^[0-9]{8}$/, example: '3XXXXXXX', maxLength: 8 },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼', pattern: /^[0-9]{8}$/, example: '5XXXXXXX', maxLength: 8 },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭', pattern: /^[0-9]{8}$/, example: '3XXXXXXX', maxLength: 8 },
  { code: '+968', country: 'Oman', flag: '🇴🇲', pattern: /^[0-9]{8}$/, example: '9XXXXXXX', maxLength: 8 },
  
  // USA
  { code: '+1', country: 'USA', flag: '🇺🇸', pattern: /^[0-9]{10}$/, example: 'XXXXXXXXXX', maxLength: 10 },
  
  // Europe (All countries)
  { code: '+44', country: 'UK', flag: '🇬🇧', pattern: /^[0-9]{10}$/, example: '7XXXXXXXXX', maxLength: 10 },
  { code: '+33', country: 'France', flag: '🇫🇷', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+49', country: 'Germany', flag: '🇩🇪', pattern: /^[0-9]{10,11}$/, example: '15XXXXXXXXX', maxLength: 11 },
  { code: '+39', country: 'Italy', flag: '🇮🇹', pattern: /^[0-9]{9,10}$/, example: '3XXXXXXXX', maxLength: 10 },
  { code: '+34', country: 'Spain', flag: '🇪🇸', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+32', country: 'Belgium', flag: '🇧🇪', pattern: /^[0-9]{9}$/, example: '4XXXXXXXX', maxLength: 9 },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+43', country: 'Austria', flag: '🇦🇹', pattern: /^[0-9]{10}$/, example: '6XXXXXXXXX', maxLength: 10 },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+47', country: 'Norway', flag: '🇳🇴', pattern: /^[0-9]{8}$/, example: '4XXXXXXX', maxLength: 8 },
  { code: '+45', country: 'Denmark', flag: '🇩🇰', pattern: /^[0-9]{8}$/, example: '2XXXXXXX', maxLength: 8 },
  { code: '+358', country: 'Finland', flag: '🇫🇮', pattern: /^[0-9]{9}$/, example: '4XXXXXXXX', maxLength: 9 },
  { code: '+353', country: 'Ireland', flag: '🇮🇪', pattern: /^[0-9]{9}$/, example: '8XXXXXXXX', maxLength: 9 },
  { code: '+351', country: 'Portugal', flag: '🇵🇹', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+30', country: 'Greece', flag: '🇬🇷', pattern: /^[0-9]{10}$/, example: '6XXXXXXXXX', maxLength: 10 },
  { code: '+48', country: 'Poland', flag: '🇵🇱', pattern: /^[0-9]{9}$/, example: '5XXXXXXXX', maxLength: 9 },
  { code: '+36', country: 'Hungary', flag: '🇭🇺', pattern: /^[0-9]{9}$/, example: '3XXXXXXXX', maxLength: 9 },
  { code: '+420', country: 'Czech Republic', flag: '🇨🇿', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+421', country: 'Slovakia', flag: '🇸🇰', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+386', country: 'Slovenia', flag: '🇸🇮', pattern: /^[0-9]{8}$/, example: '3XXXXXXX', maxLength: 8 },
  { code: '+385', country: 'Croatia', flag: '🇭🇷', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+387', country: 'Bosnia', flag: '🇧🇦', pattern: /^[0-9]{8}$/, example: '6XXXXXXX', maxLength: 8 },
  { code: '+381', country: 'Serbia', flag: '🇷🇸', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+382', country: 'Montenegro', flag: '🇲🇪', pattern: /^[0-9]{8}$/, example: '6XXXXXXX', maxLength: 8 },
  { code: '+383', country: 'Kosovo', flag: '🇽🇰', pattern: /^[0-9]{8}$/, example: '4XXXXXXX', maxLength: 8 },
  { code: '+40', country: 'Romania', flag: '🇷🇴', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+359', country: 'Bulgaria', flag: '🇧🇬', pattern: /^[0-9]{9}$/, example: '8XXXXXXXX', maxLength: 9 },
  { code: '+355', country: 'Albania', flag: '🇦🇱', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+389', country: 'North Macedonia', flag: '🇲🇰', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+373', country: 'Moldova', flag: '🇲🇩', pattern: /^[0-9]{8}$/, example: '6XXXXXXX', maxLength: 8 },
  { code: '+370', country: 'Lithuania', flag: '🇱🇹', pattern: /^[0-9]{8}$/, example: '6XXXXXXX', maxLength: 8 },
  { code: '+371', country: 'Latvia', flag: '🇱🇻', pattern: /^[0-9]{8}$/, example: '2XXXXXXX', maxLength: 8 },
  { code: '+372', country: 'Estonia', flag: '🇪🇪', pattern: /^[0-9]{7,8}$/, example: '5XXXXXXX', maxLength: 8 },
  { code: '+352', country: 'Luxembourg', flag: '🇱🇺', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+356', country: 'Malta', flag: '🇲🇹', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+354', country: 'Iceland', flag: '🇮🇸', pattern: /^[0-9]{7}$/, example: 'XXXXXXX', maxLength: 7 },
  { code: '+377', country: 'Monaco', flag: '🇲🇨', pattern: /^[0-9]{8,9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+379', country: 'Vatican City', flag: '🇻🇦', pattern: /^[0-9]{8}$/, example: '06XXXXXX', maxLength: 8 },
  { code: '+378', country: 'San Marino', flag: '🇸🇲', pattern: /^[0-9]{8,10}$/, example: '6XXXXXXXX', maxLength: 10 },
  { code: '+423', country: 'Liechtenstein', flag: '🇱🇮', pattern: /^[0-9]{7}$/, example: 'XXXXXXX', maxLength: 7 },
  { code: '+376', country: 'Andorra', flag: '🇦🇩', pattern: /^[0-9]{6,8}$/, example: '3XXXXX', maxLength: 8 },
  
  // Lebanon
  { code: '+961', country: 'Lebanon', flag: '🇱🇧', pattern: /^[0-9]{7,8}$/, example: '3XXXXXX or 7XXXXXXX', maxLength: 8 },
  
  // Africa (Selected countries including specified ones)
  { code: '+20', country: 'Egypt', flag: '🇪🇬', pattern: /^[0-9]{10}$/, example: '10XXXXXXXX', maxLength: 10 },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳', pattern: /^[0-9]{8}$/, example: '2XXXXXXX', maxLength: 8 },
  { code: '+212', country: 'Morocco', flag: '🇲🇦', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+213', country: 'Algeria', flag: '🇩🇿', pattern: /^[0-9]{9}$/, example: '5XXXXXXXX', maxLength: 9 },
  { code: '+27', country: 'South Africa', flag: '🇿🇦', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', pattern: /^[0-9]{10}$/, example: '80XXXXXXXX', maxLength: 10 },
  { code: '+254', country: 'Kenya', flag: '🇰🇪', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+255', country: 'Tanzania', flag: '🇹🇿', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+256', country: 'Uganda', flag: '🇺🇬', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+233', country: 'Ghana', flag: '🇬🇭', pattern: /^[0-9]{9}$/, example: '2XXXXXXXX', maxLength: 9 },
  { code: '+221', country: 'Senegal', flag: '🇸🇳', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+225', country: 'Ivory Coast', flag: '🇨🇮', pattern: /^[0-9]{8}$/, example: '0XXXXXXX', maxLength: 8 },
  { code: '+224', country: 'Guinea', flag: '🇬🇳', pattern: /^[0-9]{9}$/, example: '6XXXXXXXX', maxLength: 9 },
  { code: '+229', country: 'Benin', flag: '🇧🇯', pattern: /^[0-9]{8}$/, example: '9XXXXXXX', maxLength: 8 },
  { code: '+226', country: 'Burkina Faso', flag: '🇧🇫', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+250', country: 'Rwanda', flag: '🇷🇼', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+260', country: 'Zambia', flag: '🇿🇲', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼', pattern: /^[0-9]{9}$/, example: '7XXXXXXXX', maxLength: 9 },
  { code: '+265', country: 'Malawi', flag: '🇲🇼', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+261', country: 'Madagascar', flag: '🇲🇬', pattern: /^[0-9]{9}$/, example: '3XXXXXXXX', maxLength: 9 },
  { code: '+249', country: 'Sudan', flag: '🇸🇩', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+251', country: 'Ethiopia', flag: '🇪🇹', pattern: /^[0-9]{9}$/, example: '9XXXXXXXX', maxLength: 9 },
  { code: '+252', country: 'Somalia', flag: '🇸🇴', pattern: /^[0-9]{7,8}$/, example: '6XXXXXX', maxLength: 8 },
  { code: '+253', country: 'Djibouti', flag: '🇩🇯', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+257', country: 'Burundi', flag: '🇧🇮', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+258', country: 'Mozambique', flag: '🇲🇿', pattern: /^[0-9]{9}$/, example: '8XXXXXXXX', maxLength: 9 },
  { code: '+264', country: 'Namibia', flag: '🇳🇦', pattern: /^[0-9]{8,9}$/, example: '8XXXXXXX', maxLength: 9 },
  { code: '+266', country: 'Lesotho', flag: '🇱🇸', pattern: /^[0-9]{8}$/, example: '5XXXXXXX', maxLength: 8 },
  { code: '+267', country: 'Botswana', flag: '🇧🇼', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+268', country: 'Eswatini', flag: '🇸🇿', pattern: /^[0-9]{8}$/, example: '7XXXXXXX', maxLength: 8 },
  { code: '+269', country: 'Comoros', flag: '🇰🇲', pattern: /^[0-9]{7}$/, example: '3XXXXXX', maxLength: 7 },
  { code: '+290', country: 'Saint Helena', flag: '🇸🇭', pattern: /^[0-9]{4}$/, example: 'XXXX', maxLength: 4 },
  { code: '+291', country: 'Eritrea', flag: '🇪🇷', pattern: /^[0-9]{7}$/, example: '7XXXXXX', maxLength: 7 },
  { code: '+297', country: 'Aruba', flag: '🇦🇼', pattern: /^[0-9]{7}$/, example: '5XXXXXX', maxLength: 7 },
  { code: '+298', country: 'Faroe Islands', flag: '🇫🇴', pattern: /^[0-9]{6}$/, example: 'XXXXXX', maxLength: 6 },
  { code: '+299', country: 'Greenland', flag: '🇬🇱', pattern: /^[0-9]{6}$/, example: 'XXXXXX', maxLength: 6 },
  { code: '+350', country: 'Gibraltar', flag: '🇬🇮', pattern: /^[0-9]{8}$/, example: '5XXXXXXX', maxLength: 8 }
];

// Searchable Country Select Component
const SearchableCountrySelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCountry = countryCodes.find(c => c.code === value) || countryCodes[0];

  const filteredCountries = countryCodes.filter(country => 
    country.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.includes(searchTerm)
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className="w-full pl-9 pr-8 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border border-[#D1E7DD] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all cursor-pointer flex items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
      >
        <span>
          {selectedCountry.flag} {selectedCountry.code} ({selectedCountry.country})
        </span>
        <svg className="w-4 h-4 text-[#7A9C89]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-2xl border border-[#E5F0E8] overflow-hidden"
        >
          <div className="p-2 border-b border-[#E5F0E8]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search country..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#F0F7F2] border border-[#D1E7DD] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className={`px-3 py-2 cursor-pointer hover:bg-[#F0F7F2] transition-colors ${
                    value === country.code ? 'bg-[#2D6B47]/10' : ''
                  }`}
                  onClick={() => {
                    onChange(country.code);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  <span className="text-sm" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                    {country.flag} {country.code} ({country.country})
                  </span>
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-[#7A9C89] text-sm">
                No countries found
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Custom Date Picker Component - FIXED VERSION with proper blocked date handling
const CustomDatePicker = ({ value, onChange, minDate, blockedDates, blockedDatesMap, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = minDate ? new Date(minDate) : today;
  const endDate = addMonths(today, 12); // Allow booking up to 12 months ahead

  // Helper function to check if a date is Monday
  const isMonday = (date) => {
    return date.getDay() === 1; // 0 = Sunday, 1 = Monday, etc.
  };

  // Get all days to display in the calendar grid (6 rows x 7 days = 42 days)
  const getCalendarDays = () => {
    const firstDayOfMonth = startOfMonth(currentMonth);
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday
    
    // Calculate the start date of the calendar grid (the first Sunday before or on the first of the month)
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(firstDayOfMonth.getDate() - firstDayOfWeek);
    
    // Generate 42 days (6 weeks)
    const calendarDays = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      calendarDays.push(date);
    }
    
    return calendarDays;
  };

  const isDateSelectable = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Check if date is in the past (before today)
    if (isBefore(date, today)) {
      return false;
    }
    
    // Check if date is before the minimum allowed date
    if (isBefore(date, startDate)) {
      return false;
    }
    
    // Check if date is after the maximum allowed date (12 months from now)
    if (isAfter(date, endDate)) {
      return false;
    }
    
    // Check if date is blocked from database
    if (blockedDates.includes(dateStr)) {
      return false;
    }
    
    // Check if it's a Monday (restaurant closed)
    if (isMonday(date)) {
      return false;
    }
    
    return true;
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isPast = isBefore(date, today);
    const isBeforeMin = isBefore(date, startDate);
    const isAfterMax = isAfter(date, endDate);
    const isBlocked = blockedDates.includes(dateStr);
    const isMondayDate = isMonday(date);
    const isSelectable = isDateSelectable(date);
    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
    
    return {
      isPast,
      isBeforeMin,
      isAfterMax,
      isBlocked,
      isMondayDate,
      isSelectable,
      isCurrentMonth,
      isToday: isSameDay(date, today)
    };
  };

  const handleDateSelect = (date) => {
    if (isDateSelectable(date)) {
      onChange(format(date, 'yyyy-MM-dd'));
      setIsOpen(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const calendarDays = getCalendarDays();
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="relative" ref={pickerRef}>
      <div className="relative">
        <Calendar className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 sm:w-5 sm:h-5" />
        <input
          type="text"
          readOnly
          value={value ? formatDisplayDate(value) : ''}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          placeholder="Select a date"
          className={`w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border ${value && blockedDates.includes(value) ? 'border-red-300 bg-red-50' : 'border-[#D1E7DD]'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all backdrop-blur-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
          disabled={disabled}
        />
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-50 mt-2 bg-white rounded-xl shadow-2xl border border-[#E5F0E8] p-4 min-w-[320px]"
        >
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-[#F0F7F2] rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#2D6B47]" />
            </button>
            <h3 className="text-lg font-bold text-[#1A3C27]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
              {format(currentMonth, 'MMMM yyyy')}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-[#F0F7F2] rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#2D6B47]" />
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekdays.map(day => (
              <div key={day} className="text-center text-xs font-semibold text-[#7A9C89] py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((date, index) => {
              const status = getDateStatus(date);
              const dayOfMonth = date.getDate();
              const dateStr = format(date, 'yyyy-MM-dd');

              let dayClass = "p-2 text-center rounded-lg transition-all text-sm ";
              
              // Check if date is blocked or is Monday
              const isBlocked = blockedDates.includes(dateStr);
              const isMondayDate = isMonday(date);
              
              if (!status.isCurrentMonth) {
                dayClass += "text-gray-300 bg-gray-50 cursor-not-allowed opacity-50 ";
              } else if (isBlocked || isMondayDate) {
                dayClass += "bg-red-100 text-red-600 line-through cursor-not-allowed opacity-70 ";
              } else if (status.isToday) {
                dayClass += "bg-[#2D6B47]/10 text-[#2D6B47] font-bold border border-[#2D6B47]/30 ";
              } else if (value && isSameDay(date, new Date(value))) {
                dayClass += "bg-[#2D6B47] text-white font-bold ";
              } else if (!status.isSelectable && !isBlocked) {
                dayClass += "text-gray-300 cursor-not-allowed bg-gray-50 ";
              } else {
                dayClass += "hover:bg-[#F0F7F2] text-[#1A3C27] cursor-pointer hover:scale-105 ";
              }

              return (
                <button
                  key={index}
                  onClick={() => handleDateSelect(date)}
                  disabled={!status.isSelectable || !status.isCurrentMonth || isBlocked || isMondayDate}
                  className={dayClass}
                  title={isBlocked ? blockedDatesMap[dateStr] || 'This date is not available' : 
                         isMondayDate ? 'Restaurant is closed on Mondays' : ''}
                >
                  {dayOfMonth}
                  
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-[#E5F0E8] flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2D6B47] rounded"></div>
              <span className="text-[#1A3C27]">Selected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
              <span className="text-[#1A3C27]">Blocked</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-200 border border-red-400 rounded"></div>
              <span className="text-[#1A3C27]">Mondays (Closed)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#F0F7F2] border border-[#2D6B47]/30 rounded"></div>
              <span className="text-[#1A3C27]">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></div>
              <span className="text-[#1A3C27]">Past/Unavailable</span>
            </div>
          </div>

          {/* Blocked Date Info */}
          {value && blockedDates.includes(value) && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs">
              <p className="text-red-600 font-medium flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {blockedDatesMap[value] || 'This date is not available for reservations'}
              </p>
            </div>
          )}

          {/* Monday Info */}
          {value && isMonday(new Date(value)) && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg text-xs">
              <p className="text-red-600 font-medium flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Restaurant is closed on Mondays
              </p>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default function Home() {
  const [formData, setFormData] = useState({
    customer_name: '',
    countryCode: '+961',
    phoneNumber: '',
    email: '',
    date_of_birth: '',
    date: '',
    time: '',
    guests: '',
    special_requests: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [reservationId, setReservationId] = useState(null);
  const [dobError, setDobError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Blocked dates and times states
  const [blockedDatesMap, setBlockedDatesMap] = useState({});
  const [allBlockedDates, setAllBlockedDates] = useState([]);
  const [blockedTimesMap, setBlockedTimesMap] = useState({});
  const [loadingBlockedDates, setLoadingBlockedDates] = useState(true);
  
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');
  const recaptchaRef = useRef(null);

  // Available time slots
  const timeSlots = ['5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM', '12:00 AM', '1:00 AM'];
  
  // Available guest options
  const guestOptions = [1,2,3, 4,5,  6,7,  8,9,  10];

  // Helper function to check if a date is Monday
  const isMonday = (date) => {
    return date.getDay() === 1; // 0 = Sunday, 1 = Monday, etc.
  };

  // Restaurant info
  const restaurantInfo = {
    name: "HAI Beirut",
    location: "A Tower, Antelias Rd, Beirut",
    phone: "+961 70 882 206",
    email: "reservations@haibeirut.com",
    website: "https://haibeirut.com "
  };

  // Terms and Conditions
  const termsAndConditions = [
    {
      title: "Privacy Policy",
      content: "Any personal information received through the contact form may be used to communicate with you about this service or other future services."
    },
    {
      title: "Confirmation Policy",
      content: "Until a confirmation has been issued, the Club will continue to offer available seating to other interested parties."
    },
    {
      title: "Reservation Policy",
      content: `On reservation, the table will be reserved for 30 minutes, guests to be seated within 15 minutes, late arrival may impact on seating time. Failing to arrive at the reserved time may result in your reserved table to be made available to other guests. The reservations are allowed only inside the restaurant.
      
Set-up allows for tables 2 - 6 - 8 - 12 guests, please contact the club for increased numbers via our contact numbers.
Location: HAI Beirut - A Tower, Antelias Rd, Beirut 1201, Lebanon
Mobile: +961 70 882 206 
Note request on subject line, we will check availability.`
    },
    {
      title: "Cancellation Policy",
      content: "On cancellation of your reservation, please do so 60 minutes before the service starting date and time."
    },
    {
      title: "Health & Safety",
      content: "We commit to implement all health requirements in accordance with the instructions of the Ministry of Health. No food and beverage, celebration items from outside allowed."
    },
    {
      title: "Terms Modification",
      content: "We reserve the right to change any of our terms and conditions at any time without notifying you, via updating our website."
    }
  ];

  const [minDate, setMinDate] = useState('');

  const fetchBlockedDates = async () => {
    try {
      setLoadingBlockedDates(true);
      
      const { data: datesData, error: datesError } = await supabase
        .from('blocked_dates')
        .select('*');
        
      if (datesError) throw datesError;
      
      const { data: timesData, error: timesError } = await supabase
        .from('blocked_times')
        .select('*');
        
      if (timesError) throw timesError;
      
      const blockedMap = {};
      const allDates = [];
      const timesMap = {};
      
      // Process blocked dates and ranges
      datesData?.forEach(item => {
        if (item.is_range) {
          const startDate = new Date(item.start_date);
          const endDate = new Date(item.end_date);
          const rangeDates = eachDayOfInterval({ start: startDate, end: endDate });
          
          rangeDates.forEach(date => {
            const dateStr = format(date, 'yyyy-MM-dd');
            if (new Date(dateStr) >= new Date(minDate || new Date())) {
              allDates.push(dateStr);
              blockedMap[dateStr] = item.reason || 'This date range is not available for reservations';
            }
          });
        } else {
          const dateStr = item.date;
          if (new Date(dateStr) >= new Date(minDate || new Date())) {
            allDates.push(dateStr);
            blockedMap[dateStr] = item.reason || 'This date is not available for reservations';
          }
        }
      });
      
      // Process blocked times
      timesData?.forEach(item => {
        const dateStr = item.date;
        if (!timesMap[dateStr]) {
          timesMap[dateStr] = [];
        }
        timesMap[dateStr].push({
          time: item.time,
          reason: item.reason || 'This time slot is not available'
        });
      });
      
      setBlockedDatesMap(blockedMap);
      setAllBlockedDates(allDates);
      setBlockedTimesMap(timesMap);
      
    } catch (error) {
    } finally {
      setLoadingBlockedDates(false);
    }
  };

  useEffect(() => {
  const startDate = new Date('2025-03-18');
  const year = startDate.getFullYear();
  const month = String(startDate.getMonth() + 1).padStart(2, '0');
  const day = String(startDate.getDate()).padStart(2, '0');
  setMinDate(`${year}-${month}-${day}`);
}, []);

  useEffect(() => {
    if (minDate) {
      fetchBlockedDates();
    }
  }, [minDate]);

  const isDateBlocked = (dateString) => {
    // Check if date is Monday
    const date = new Date(dateString);
    if (isMonday(date)) {
      return true;
    }
    return allBlockedDates.includes(dateString);
  };

  const getBlockedDateReason = (dateString) => {
    const date = new Date(dateString);
    if (isMonday(date)) {
      return 'Restaurant is closed on Mondays';
    }
    return blockedDatesMap[dateString] || 'This date is not available for reservations';
  };

  const isTimeSlotBlocked = (date, time) => {
    if (!date || !time || !blockedTimesMap[date]) {
      return false;
    }
    return blockedTimesMap[date].some(slot => slot.time === time);
  };

  const getBlockedTimeReason = (date, time) => {
    if (!date || !time || !blockedTimesMap[date]) return '';
    const slot = blockedTimesMap[date].find(s => s.time === time);
    return slot ? slot.reason : '';
  };

  const getSelectedCountry = () => {
    return countryCodes.find(c => c.code === formData.countryCode) || countryCodes[0];
  };

  const validatePhoneNumber = (code, number) => {
    const country = countryCodes.find(c => c.code === code);
    if (!country) return { valid: false, error: 'Invalid country code' };
    
    if (!number || number.trim() === '') {
      return { valid: false, error: 'Phone number is required' };
    }
    
    const digitsOnly = number.replace(/\D/g, '');
    
    if (digitsOnly.length === 0) {
      return { valid: false, error: 'Please enter a valid phone number' };
    }
    
    if (!country.pattern.test(digitsOnly)) {
      return { 
        valid: false, 
        error: `Please enter a valid ${country.country} phone number (e.g., ${country.example})` 
      };
    }
    
    return { valid: true, formatted: digitsOnly };
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return { valid: false, error: 'Date of birth is required' };
    
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dob.match(regex);
    
    if (!match) {
      return { valid: false, error: 'Please use DD/MM/YYYY format (e.g., 15/05/1990)' };
    }
    
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const year = parseInt(match[3], 10);
    
    const date = new Date(year, month, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return { valid: false, error: 'Please enter a valid date' };
    }
    
    const today = new Date();
    if (date > today) {
      return { valid: false, error: 'Date of birth cannot be in the future' };
    }
    
    let age = today.getFullYear() - year;
    const monthDiff = today.getMonth() - month;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
      age--;
    }
    
    if (age < 21) {
      return { valid: false, error: 'You must be at least 21 years old to make a reservation' };
    }
    
    if (age > 120) {
      return { valid: false, error: 'Please enter a valid date of birth' };
    }
    
    return { valid: true, date: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return { valid: false, error: 'Email address is required' };
    }
    
    // Simple email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return { valid: false, error: 'Please enter a valid email address (e.g., name@domain.com)' };
    }
    
    return { valid: true };
  };

  const verifyRecaptcha = async (token) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-recaptcha', {
        body: { token }
      });
      if (error) throw error;
      return data.success;
    } catch (error) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setDobError('');
    setPhoneError('');
    setEmailError('');
    setRecaptchaError('');
    
    // Validate email (now required)
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setEmailError(emailValidation.error);
      setSubmitError(emailValidation.error);
      return;
    }
    
    if (!RECAPTCHA_SITE_KEY) {
      setRecaptchaError('reCAPTCHA is not configured. Please contact support.');
      return;
    }
    
    if (!recaptchaToken) {
      setRecaptchaError('Please complete the reCAPTCHA verification');
      return;
    }
    
    const phoneValidation = validatePhoneNumber(formData.countryCode, formData.phoneNumber);
    if (!phoneValidation.valid) {
      setPhoneError(phoneValidation.error);
      setSubmitError(phoneValidation.error);
      return;
    }
    
    const dobValidation = validateDateOfBirth(formData.date_of_birth);
    if (!dobValidation.valid) {
      setDobError(dobValidation.error);
      setSubmitError(dobValidation.error);
      return;
    }
    
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (selectedDate < today) {
      setSubmitError('Please select a future date. Past dates cannot be booked.');
      return;
    }
    
    if (isDateBlocked(formData.date)) {
      const reason = getBlockedDateReason(formData.date);
      setSubmitError(`This date is not available for reservations. ${reason}`);
      return;
    }
    
    if (!formData.time) {
      setSubmitError('Please select a time slot.');
      return;
    }
    
    if (isTimeSlotBlocked(formData.date, formData.time)) {
      const reason = getBlockedTimeReason(formData.date, formData.time);
      setSubmitError(`This time slot is not available. ${reason}`);
      return;
    }
    
    if (!formData.guests) {
      setSubmitError('Please select the number of guests.');
      return;
    }

    if (!acceptTerms) {
      setSubmitError('You must accept the Terms & Conditions to proceed.');
      return;
    }

    setIsSubmitting(true);

    try {
      const isValidRecaptcha = await verifyRecaptcha(recaptchaToken);
      
      if (!isValidRecaptcha) {
        setRecaptchaError('reCAPTCHA verification failed. Please try again.');
        setIsSubmitting(false);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
          setRecaptchaToken(null);
        }
        return;
      }

      const fullPhoneNumber = `${formData.countryCode}${phoneValidation.formatted}`;

      const { data, error } = await supabase
        .from('reservations')
        .insert({
          customer_name: formData.customer_name,
          phone: fullPhoneNumber,
          email: formData.email,
          date_of_birth: dobValidation.date,
          date: formData.date,
          time: formData.time,
          guests: parseInt(formData.guests),
          special_requests: formData.special_requests,
          created_at: new Date().toISOString(),
          status: 'pending',
          recaptcha_verified: true,
          recaptcha_token: recaptchaToken,
          recaptcha_verified_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to submit reservation');
      }

      setReservationId(data.id);

      setIsSubmitted(true);
      
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit reservation. Please try again.');
      
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setRecaptchaError('');
  };

  const onRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setRecaptchaError('reCAPTCHA expired. Please verify again.');
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, '');
    const country = getSelectedCountry();
    const limitedDigits = digitsOnly.substring(0, country.maxLength);
    
    setFormData({
      ...formData,
      phoneNumber: limitedDigits
    });
    
    if (phoneError) {
      setPhoneError('');
    }
  };

  const handleCountryCodeChange = (code) => {
    setFormData({
      ...formData,
      countryCode: code,
      phoneNumber: ''
    });
    setPhoneError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    const fieldMap = {
      'name': 'customer_name',
      'time': 'time',
      'requests': 'special_requests',
      'dob': 'date_of_birth',
      'email': 'email'
    };
    
    const dbFieldName = fieldMap[name] || name;
    
    if (name === 'date' && value) {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        setSubmitError('Please select a future date. Past dates cannot be booked.');
        return;
      }
      
      if (isDateBlocked(value)) {
        const reason = getBlockedDateReason(value);
        setSubmitError(`This date is not available for reservations. ${reason}`);
        return;
      }
      
      if (formData.time && isTimeSlotBlocked(value, formData.time)) {
        setFormData(prev => ({ ...prev, time: '' }));
        setSubmitError('The previously selected time is not available for this date. Please select a new time.');
      } else {
        setSubmitError('');
      }
    }
    
    if (name === 'time' && formData.date) {
      if (isTimeSlotBlocked(formData.date, value)) {
        const reason = getBlockedTimeReason(formData.date, value);
        setSubmitError(`This time slot is not available. ${reason}`);
      } else {
        setSubmitError('');
      }
    }
    
    if (name === 'dob') {
      setDobError('');
    }
    
    if (name === 'email') {
      setEmailError('');
    }
    
    setFormData({
      ...formData,
      [dbFieldName]: value
    });
    
    if (submitError && name !== 'time' && name !== 'date' && name !== 'email') {
      setSubmitError('');
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setReservationId(null);
    setDobError('');
    setPhoneError('');
    setEmailError('');
    setRecaptchaError('');
    setFormData({
      customer_name: '',
      countryCode: '+961',
      phoneNumber: '',
      email: '',
      date_of_birth: '',
      date: '',
      time: '',
      guests: '',
      special_requests: ''
    });
    setAcceptTerms(false);
    setSubmitError('');
    
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setRecaptchaToken(null);
    }
  };

  const handleDobChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    value = value.substring(0, 8);
    
    if (value.length > 4) {
      value = `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4, 8)}`;
    } else if (value.length > 2) {
      value = `${value.substring(0, 2)}/${value.substring(2, 4)}`;
    } else if (value.length > 0) {
      value = value.substring(0, 2);
    }
    
    setFormData({
      ...formData,
      date_of_birth: value
    });
    
    if (dobError) {
      setDobError('');
    }
  };

  const TermsModal = () => (
    <AnimatePresence>
      {showTermsModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowTermsModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-[#E5F0E8] p-4 sm:p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#2D6B47]" />
                <h3 className="text-xl sm:text-2xl font-bold text-[#1A3C27]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800 }}>
                  Terms & Conditions
                </h3>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                className="p-2 hover:bg-[#F0F7F2] rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-[#1A3C27]" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                {termsAndConditions.map((term, index) => (
                  <div key={index} className="border-b border-[#E5F0E8] pb-4 last:border-b-0">
                    <h4 className="text-lg font-bold text-[#1A3C27] mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      {term.title}
                    </h4>
                    <p className="text-[#1A3C27] whitespace-pre-line text-sm sm:text-base" style={{ fontWeight: 500 }}>
                      {term.content}
                    </p>
                  </div>
                ))}
                
                <div className="mt-6 pt-6 border-t border-[#E5F0E8]">
                  <p className="text-[#1A3C27] text-center font-medium" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
                    Thank you,
                  </p>
                  <p className="text-[#2D6B47] text-center font-bold text-lg mt-1" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800 }}>
                    Hai Beirut Rooftop
                  </p>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm border-t border-[#E5F0E8] p-4">
              <div className="flex items-center justify-center">
                <button
                  onClick={() => {
                    setAcceptTerms(true);
                    setShowTermsModal(false);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-[#2D6B47] to-[#4CAF50] text-white font-semibold rounded-lg sm:rounded-xl hover:opacity-90 transition-all duration-300"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
                >
                  Accept Terms & Continue
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={restaurantBackground}
            alt="Restaurant Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C2418]/90 via-[#1A3C27]/80 to-[#2D6B47]/70"></div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl shadow-[#000]/20 p-8 border border-white/20 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-[#2D6B47]/10 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle2 className="w-10 h-10 text-[#2D6B47]" />
            </motion.div>
            
            <h3 className="text-2xl font-bold text-[#1A3C27] mb-3" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800 }}>
              Reservation Submitted!
            </h3>
            
            <p className="text-[#1A3C27] mb-4" style={{ fontWeight: 500 }}>
              Thank you, <span className="font-semibold" style={{ fontWeight: 700 }}>{formData.customer_name}</span>!
            </p>
            
            <div className="bg-[#F0F7F2] rounded-xl p-4 my-4">
              <p className="text-sm text-[#2D6B47] mb-1" style={{ fontWeight: 600 }}>
                <span className="font-semibold" style={{ fontWeight: 700 }}>{formData.guests} guests</span> • {formData.date && format(new Date(formData.date), 'MMMM d, yyyy')} • {formData.time}
              </p>
              {reservationId && (
                <p className="text-xs text-[#2D6B47] mt-2" style={{ fontWeight: 500 }}>
                  Reservation ID: <span className="font-mono font-bold" style={{ fontWeight: 700 }}>{reservationId}</span>
                </p>
              )}
            </div>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-full text-sm font-medium mb-6" style={{ fontWeight: 600 }}>
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
              Pending admin approval - we'll contact you soon
            </div>
            
            <p className="text-sm text-[#6B6B6B] mb-2" style={{ fontWeight: 500 }}>
              Your reservation request has been received and is awaiting confirmation.
            </p>
            <p className="text-sm text-[#6B6B6B] mb-6" style={{ fontWeight: 500 }}>
              We will contact you at <span className="font-semibold" style={{ fontWeight: 700 }}>{formData.email}</span> once it's confirmed
            </p>
            
            <div className="mt-6 pt-6 border-t border-[#E5F0E8]">
              <div className="flex items-center justify-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-[#2D6B47]" />
                <p className="text-[#1A3C27] text-sm font-medium" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
                  {restaurantInfo.location}
                </p>
              </div>
              <p className="text-[#1A3C27] text-sm font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
                Need immediate assistance?
              </p>
              <div className="text-sm text-[#2D6B47] space-y-1" style={{ fontWeight: 500 }}>
                <p>Call us: <span className="font-semibold" style={{ fontWeight: 700 }}>{restaurantInfo.phone}</span></p>
                <p>Email: <span className="font-semibold" style={{ fontWeight: 700 }}>{restaurantInfo.email}</span></p>
              </div>
            </div>
            
            <button
              onClick={resetForm}
              className="w-full py-3 mt-6 bg-gradient-to-r from-[#2D6B47] to-[#4CAF50] text-white font-semibold rounded-xl hover:opacity-90 transition-all duration-300"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
            >
              Make Another Reservation
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex flex-col relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={restaurantBackground}
            alt="Restaurant Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0C2418]/90 via-[#1A3C27]/80 to-[#2D6B47]/70"></div>
          
          <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-br from-[#4CAF50]/10 to-[#2D6B47]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-tr from-[#2D6B47]/10 to-[#1A3C27]/5 rounded-full blur-3xl"></div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="absolute top-20 left-10 text-[#4CAF50]/20 z-20"
        >
          <Leaf size={48} />
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="absolute top-10 right-20 text-[#4CAF50]/15 z-20"
        >
          <ChefHat size={40} />
        </motion.div>

        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full max-w-lg sm:max-w-2xl"
          >
            <div className="text-center mb-8 sm:mb-12">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative mx-auto mb-4 sm:mb-6"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#4CAF50]/20 to-[#2D6B47]/20 rounded-full blur-xl"></div>
                  <img 
                    src={logo}
                    alt="Hai Beirut Restaurant Logo"
                    className="relative w-32 h-32 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain mx-auto drop-shadow-2xl"
                  />
                </div>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-3xl sm:text-4xl text-white font-black mb-2 tracking-wide" 
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
              >
                {restaurantInfo.name}
              </motion.h1>
              
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: '150px' }}
                transition={{ duration: 1, delay: 0.5 }}
                className="mx-auto h-1 bg-gradient-to-r from-transparent via-[#4CAF50]/60 to-transparent mt-2 sm:mt-3 rounded-full"
              ></motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#4CAF50]/20 to-[#2D6B47]/20 rounded-2xl sm:rounded-3xl blur-2xl"></div>
              
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl shadow-[#000]/20 p-5 sm:p-8 md:p-10 border border-white/20 relative">
                <div className="text-center mb-6 sm:mb-8">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#1A3C27]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                    Reserve Your Table
                  </h2>
                </div>

                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 text-sm font-medium">
                    {submitError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div>
                    <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        name="name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border border-[#D1E7DD] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all backdrop-blur-sm"
                        required
                        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      Phone Number
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative sm:w-2/5">
                        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 z-10" />
                        <SearchableCountrySelect
                          value={formData.countryCode}
                          onChange={handleCountryCodeChange}
                        />
                      </div>
                      <div className="relative sm:w-3/5">
                        <input
                          type="tel"
                          value={formData.phoneNumber}
                          onChange={handlePhoneNumberChange}
                          placeholder={getSelectedCountry().example}
                          className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border ${phoneError ? 'border-red-300 focus:ring-red-500/40' : 'border-[#D1E7DD] focus:ring-[#2D6B47]/40'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:border-[#2D6B47] transition-all backdrop-blur-sm`}
                          required
                          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                        />
                      </div>
                    </div>
                    {phoneError && (
                      <p className="text-red-600 text-xs mt-1 ml-1 font-medium">
                        {phoneError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className={`w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border ${emailError ? 'border-red-300 focus:ring-red-500/40' : 'border-[#D1E7DD] focus:ring-[#2D6B47]/40'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:border-[#2D6B47] transition-all backdrop-blur-sm`}
                        required
                        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                      />
                    </div>
                    {emailError && (
                      <p className="text-red-600 text-xs mt-1 ml-1 font-medium">
                        {emailError}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      Date of Birth <span className="text-[#7A9C89] font-semibold" style={{ fontWeight: 600 }}>(DD/MM/YYYY)</span>
                    </label>
                    <div className="relative">
                      <Cake className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        name="dob"
                        value={formData.date_of_birth}
                        onChange={handleDobChange}
                        placeholder="DD/MM/YYYY"
                        maxLength={10}
                        className={`w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border ${dobError ? 'border-red-300 focus:ring-red-500/40' : 'border-[#D1E7DD] focus:ring-[#2D6B47]/40'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:border-[#2D6B47] transition-all backdrop-blur-sm`}
                        required
                        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                      />
                    </div>
                    {dobError && (
                      <p className="text-red-600 text-xs mt-1 ml-1 font-medium">
                        {dobError}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                        Date
                      </label>
                      <CustomDatePicker
                        value={formData.date}
                        onChange={(date) => {
                          setFormData({ ...formData, date });
                          setSubmitError('');
                        }}
                        minDate={minDate}
                        blockedDates={allBlockedDates}
                        blockedDatesMap={blockedDatesMap}
                        disabled={loadingBlockedDates}
                      />
                      {loadingBlockedDates && (
                        <p className="text-xs text-[#7A9C89] mt-1 ml-1 flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin" /> Loading calendar...
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                        Time
                      </label>
                      <div className="relative">
                        <Clock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 sm:w-5 sm:h-5 z-10" />
                        <select
                          name="time"
                          value={formData.time}
                          onChange={handleChange}
                          className={`w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all appearance-none backdrop-blur-sm text-[#1A3C27] cursor-pointer ${
                            formData.time && isTimeSlotBlocked(formData.date, formData.time) 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-[#D1E7DD]'
                          }`}
                          required
                          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                          disabled={!formData.date || loadingBlockedDates}
                        >
                          <option value="" className="text-[#7A9C89] font-medium" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                            {!formData.date ? 'Select a date first' : 'Select a time slot'}
                          </option>
                          {formData.date && timeSlots.map((time) => {
                            const isBlocked = isTimeSlotBlocked(formData.date, time);
                            return (
                              <option 
                                key={time} 
                                value={time}
                                disabled={isBlocked}
                                className={isBlocked ? 'text-red-400 bg-red-50 line-through' : 'text-[#1A3C27]'}
                              >
                                {time} {isBlocked ? ' (Not Available)' : ''}
                              </option>
                            );
                          })}
                        </select>
                        <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <svg className="w-4 h-4 text-[#7A9C89]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      
                      {formData.date && formData.time && isTimeSlotBlocked(formData.date, formData.time) && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs">
                          <p className="text-red-600 font-medium flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {getBlockedTimeReason(formData.date, formData.time)}
                          </p>
                        </div>
                      )}
                      
                      {formData.date && !loadingBlockedDates && (
                        <p className="text-xs text-[#7A9C89] mt-1 ml-1">
                          {timeSlots.length - (blockedTimesMap[formData.date]?.length || 0)} time slots available
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      Guests
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-[#7A9C89] w-4 h-4 sm:w-5 sm:h-5 z-10" />
                      <select
                        name="guests"
                        value={formData.guests}
                        onChange={handleChange}
                        className="w-full pl-9 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border border-[#D1E7DD] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all appearance-none backdrop-blur-sm text-[#1A3C27] cursor-pointer"
                        required
                        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                      >
                        <option value="" className="text-[#7A9C89] font-medium" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                          Number of guests
                        </option>
                        {guestOptions.map((num) => (
                          <option key={num} value={num} className="text-[#1A3C27] font-medium" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
                            {num} {num === 1 ? 'person' : 'people'}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-[#7A9C89]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1A3C27] font-bold mb-1 sm:mb-2 text-sm sm:text-base" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      Special Requests <span className="text-[#7A9C89] font-semibold" style={{ fontWeight: 600 }}>(optional)</span>
                    </label>
                    <textarea
                      name="requests"
                      value={formData.special_requests}
                      onChange={handleChange}
                      placeholder="Any dietary requirements, special occasions, or seating preferences..."
                      rows="3"
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white/80 border border-[#D1E7DD] rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D6B47]/40 focus:border-[#2D6B47] transition-all resize-none backdrop-blur-sm"
                      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
                    />
                  </div>

                  <div className="pt-2">
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <input
                          type="checkbox"
                          id="terms"
                          checked={acceptTerms}
                          onChange={(e) => setAcceptTerms(e.target.checked)}
                          className="sr-only"
                        />
                        <div 
                          className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer transition-all duration-200 ${acceptTerms ? 'bg-[#2D6B47] border-[#2D6B47]' : 'bg-white border-[#D1E7DD] hover:border-[#2D6B47]'}`}
                          onClick={() => setAcceptTerms(!acceptTerms)}
                        >
                          {acceptTerms && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                      <label htmlFor="terms" className="text-sm text-[#1A3C27] cursor-pointer flex-1 font-medium" style={{ fontWeight: 400 }}>
                        <span>
                          I agree to the{' '}
                          <button
                            type="button"
                            onClick={() => setShowTermsModal(true)}
                            className="text-[#2D6B47] font-bold hover:underline focus:outline-none"
                            style={{ fontWeight: 700 }}
                          >
                            Terms & Conditions
                          </button>
                          {' '}including Privacy, Reservation, and Cancellation policies
                        </span>
                      </label>
                    </div>
                    {!acceptTerms && submitError && submitError.includes('Terms') && (
                      <p className="text-red-600 text-xs mt-1 ml-8 font-medium">
                        You must accept the Terms & Conditions to proceed
                      </p>
                    )}
                  </div>

                  {RECAPTCHA_SITE_KEY ? (
                    <div className="flex flex-col items-center justify-center pt-2">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={onRecaptchaChange}
                        onExpired={onRecaptchaExpired}
                        theme="light"
                        size="normal"
                      />
                      {recaptchaError && (
                        <p className="text-red-600 text-xs mt-2 font-medium">{recaptchaError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 p-3 rounded-lg text-sm text-center font-medium">
                      ⚠️ reCAPTCHA configuration missing. Please contact support.
                    </div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={isSubmitting || (formData.date && isDateBlocked(formData.date)) || (formData.date && formData.time && isTimeSlotBlocked(formData.date, formData.time))}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className={`w-full py-3 sm:py-4 text-sm sm:text-base font-bold text-white rounded-lg sm:rounded-xl shadow-lg transition-all duration-300 relative overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed ${
                      (formData.date && isDateBlocked(formData.date)) || (formData.date && formData.time && isTimeSlotBlocked(formData.date, formData.time))
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#2D6B47] to-[#4CAF50] hover:shadow-xl hover:shadow-[#2D6B47]/40'
                    }`}
                    style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, letterSpacing: '0.05em' }}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center font-bold">
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </span>
                    ) : formData.date && isDateBlocked(formData.date) ? (
                      <span className="relative z-10 font-bold">Date Not Available</span>
                    ) : formData.date && formData.time && isTimeSlotBlocked(formData.date, formData.time) ? (
                      <span className="relative z-10 font-bold">Time Not Available</span>
                    ) : (
                      <span className="relative z-10 font-bold ">Reserve Table</span>
                    )}
                    {!(formData.date && isDateBlocked(formData.date)) && !(formData.date && formData.time && isTimeSlotBlocked(formData.date, formData.time)) && (
                      <div className="absolute inset-0 bg-gradient-to-r from-[#4CAF50] to-[#2D6B47] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    )}
                  </motion.button>
                </form>

                <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-[#E5F0E8] text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <MapPin className="w-4 h-4 text-[#2D6B47]" />
                    <p className="text-[#1A3C27] text-sm sm:text-base font-bold" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                      {restaurantInfo.location}
                    </p>
                  </div>
                  
                  <p className="text-[#1A3C27] mb-1 sm:mb-2 text-sm sm:text-base font-bold" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                    <strong className="font-black" style={{ fontWeight: 900 }}>Need immediate assistance?</strong>
                  </p>
                  <p className="text-[#2D6B47] text-xs sm:text-sm md:text-base font-semibold" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
                    Call us: <span className="font-bold" style={{ fontWeight: 700 }}>{restaurantInfo.phone}</span> 
                    <br className="sm:hidden" />
                    <span className="hidden sm:inline"> • </span>
                    Email: <span className="font-bold" style={{ fontWeight: 700 }}>{restaurantInfo.email}</span>
                  </p>
                </div>
              </div>
            </motion.div>

            <div className="text-center mt-6 sm:mt-8">
              <p className="text-sm text-white/80 tracking-wider font-medium" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, letterSpacing: '0.1em' }}>
                © {new Date().getFullYear()} {restaurantInfo.name}
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <TermsModal />
    </>
  );
}